import React from "react";
import PropTypes from "prop-types";
import { getIn, useFormikContext } from "formik";
import { Form } from "semantic-ui-react";
import NestedVocabularyField from "./NestedVocabularyField";

/**
 * Vocabulary object autocomplete widget with readonly sub-field display.
 *
 * Wraps NestedVocabularyField (which stores {id, schema, url, label} on
 * selection) and shows the 4 tuple fields as readonly inputs below the
 * dropdown whenever a value is set. The inputs update reactively when the
 * user picks a different item or clears the selection.
 *
 * Use this widget instead of AutocompleteDropdown when you want the stored
 * vocabulary object metadata to be visible to the depositor.
 */
const VocabularyObjectAutocomplete = ({
  fieldPath,
  label,
  icon,
  required,
  description,
  vocabulary_id,
  autocompleteFrom,
  autocompleteFromAcceptHeader,
  clearable,
  placeholder,
}) => {
  const { values } = useFormikContext();
  const currentValue = getIn(values, fieldPath, null);
  const hasValue =
    currentValue && typeof currentValue === "object" && currentValue.id;

  // Reconstruct property shape expected by NestedVocabularyField.
  const property = {
    type: "vocabulary_object",
    vocabulary_id,
    autocompleteFrom,
    autocompleteFromAcceptHeader,
    clearable,
    placeholder,
  };

  return (
    <>
      {/* NestedVocabularyField renders its own label (associated with the
          dropdown's internal __autocomplete_id path). Do not add another
          label here — it would be disconnected and duplicate. */}
      <NestedVocabularyField
        fieldPath={fieldPath}
        property={property}
        label={label}
        required={required}
      />
      {hasValue && (
        <div
          className="vocabulary-object-subfields"
          style={{ marginTop: "0.5rem" }}
        >
          <Form.Group widths="equal">
            <Form.Input
              id={`${fieldPath}__display_id`}
              label="ID"
              readOnly
              value={currentValue.id || ""}
              fluid
              className="vocabulary-object-readonly-field"
            />
            <Form.Input
              id={`${fieldPath}__display_label`}
              label="Label"
              readOnly
              value={currentValue.label || ""}
              fluid
              className="vocabulary-object-readonly-field"
            />
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Input
              id={`${fieldPath}__display_schema`}
              label="Schema"
              readOnly
              value={currentValue.schema || ""}
              fluid
              className="vocabulary-object-readonly-field"
            />
            <Form.Input
              id={`${fieldPath}__display_url`}
              label="URL"
              readOnly
              value={currentValue.url || ""}
              fluid
              className="vocabulary-object-readonly-field"
            />
          </Form.Group>
        </div>
      )}
      {description && <label className="helptext">{description}</label>}
    </>
  );
};

VocabularyObjectAutocomplete.propTypes = {
  fieldPath: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.string,
  required: PropTypes.bool,
  description: PropTypes.string,
  vocabulary_id: PropTypes.string,
  autocompleteFrom: PropTypes.string,
  autocompleteFromAcceptHeader: PropTypes.string,
  clearable: PropTypes.bool,
  placeholder: PropTypes.string,
};

VocabularyObjectAutocomplete.defaultProps = {
  icon: undefined,
  required: false,
  description: undefined,
  vocabulary_id: undefined,
  autocompleteFrom: undefined,
  autocompleteFromAcceptHeader: "application/vnd.inveniordm.v1+json",
  clearable: true,
  placeholder: undefined,
};

export default VocabularyObjectAutocomplete;
